<template>
  <p-button
    class="ml-2 p-button-rounded p-button-icon-only p-button-secondary social-button"
    type="button"
    aria-haspopup="true"
    aria-controls="social_menu"
    @click="toggleSocialMenu"
  >
    <div class="social-button__icon social-button__icon_type_ellipsis" />
  </p-button>
  <p-menu
    id="social_menu"
    ref="menu"
    class="social-menu"
    :model="menuItems"
    :popup="true"
  >
    <template #item="{ item }">
      <a
        v-if="item.url"
        :href="item.url"
        class="p-menuitem-link social-menu__item"
        target="_blank"
        role="menuitem"
        tabindex="0"
        rel="noreferrer noopener"
        @click="item.command"
      >
        <span class="p-menuitem-icon" :class="item.icon"></span>
        <span class="p-menuitem-text">{{ item.label }}</span>
      </a>
      <router-link
        v-else
        class="p-menuitem-link social-menu__item"
        :class="{ active: route.name === item.name }"
        :to="{ name: item.name }"
        @click="
          () => {
            if (item.command) {
              item.command();
            }
          }
        "
      >
        <span v-if="item.icon" class="p-menuitem-icon" :class="item.icon" />
        {{ item.label }}
      </router-link>
    </template>
  </p-menu>
</template>

<script setup lang="ts">
import {
  ChainsEnum,
  CORRECT_CHAIN,
  PAGE_NAMES_WITH_ONBOARDING,
} from '@/constants';
import { useStore } from '@/store';
import { computed, ref } from 'vue';
import { RouteRecordName, useRoute } from 'vue-router';
import { useI18n } from '@/composables/useI18n';

const { t } = useI18n();

const route = useRoute();

const mainStore = useStore();
const menu = ref();
const toggleSocialMenu = (event: MouseEvent) => {
  menu.value.toggle(event);
};

const restoreOnBoarding = (value: boolean) => {
  menu.value.hide();
  mainStore.showOnBoarding.value = value;
};

const isMovement = computed(() =>
  [ChainsEnum['movement-testnet'], ChainsEnum['movement-mainnet']].includes(
    CORRECT_CHAIN.id,
  ),
);

const menuItems = computed(() => {
  let items = [
    {
      icon: 'menu-link menu-link_type_dashboard',
      label: t('common.dashboard'),
      name: 'dashboard',
      to: { name: 'dashboard' },
      command: () => menu.value.hide(),
    },
    {
      icon: 'pi pi-info-circle',
      label: t('header.learnMore'),
      url: 'https://home.liquidswap.com',
    },
    !isMovement.value && {
      icon: 'pi pi-plus-circle',
      label: t('header.createToken'),
      name: 'token-generation',
      to: { name: 'token-generation' },
      command: () => menu.value.hide(),
    },
  ].filter(Boolean);
  if (
    mainStore.showOnBoarding.value === false &&
    PAGE_NAMES_WITH_ONBOARDING.includes(route.name as RouteRecordName)
  ) {
    items.push({
      icon: 'onboarding-icon',
      label: t('header.onboarding'),
      url: undefined,
      target: null,
      command: () => restoreOnBoarding(true),
    });
  }
  items.push({
    icon: 'pi pi-book',
    label: t('header.docs'),
    url: 'https://docs.liquidswap.com',
  });
  if (CORRECT_CHAIN.id === ChainsEnum['aptos-testnet']) {
    items.push({
      icon: 'pi pi-money-bill',
      label: t('header.mainnet'),
      url: 'https://liquidswap.com',
    });
  }

  items = items.concat(
    {
      icon: 'pi pi-discord',
      label: t('header.discord'),
      url: 'https://discord.com/invite/44QgPFHYqs',
    },
    {
      icon: 'pi pi-comments',
      label: t('header.feedbackForPartners'),
      url: 'https://form.typeform.com/to/ggYvlrgj',
    },
    {
      icon: 'pi pi-telegram',
      label: t('header.telegramCommunity'),
      url: 'https://t.me/pontemnetworkchat',
    },
  );
  return items;
});
</script>

<style lang="scss">
.p-menu.social-menu {
  padding: 0.5rem;

  border-radius: 0.75rem;

  background-color: #28253e;
}

.p-menuitem-link.social-menu__item {
  border-radius: 0.5rem;

  transition: background-color 0.15s ease;
  will-change: background-color;

  &:hover {
    background-color: #3e3e52 !important;
  }
}

.menu-link {
  width: 18px;
  height: 18px;

  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;

  &_type_dashboard {
    background-image: url('@/assets/dashboard.svg');
  }
}
</style>
